import { Fragment } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Image } from '@hivebrite/design-system/Image';
import { FakeLink } from '@hivebrite/design-system/Link';
import { useTheme, useBreakpoint } from '@hivebrite/design-system/Theme';
import { useToggle } from '@hivebrite/react-hooks';

import StatusButton from 'hb-react/frontoffice/apps/Mentoring/components/StatusButton';
import {
  type MentorProfile,
  type Program,
} from 'hb-react/frontoffice/apps/Mentoring/types';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import FolderDetailsModal from 'hb-react/shared/components/FolderPageHeader/FolderDetailsModal';
import { useTextTruncation } from 'hb-react/shared/hooks/useTextTruncation';
import ThemeProvider from 'hb-react/style/ThemeProvider';

interface PageHeaderProps {
  disabledAccess?: boolean;
  program: Program;
  status?: MentorProfile['status'] | null;
}

const PageHeader = ({ program, status, disabledAccess }: PageHeaderProps) => {
  const { currentUser } = useDefaultAppContexts();
  const {
    name,
    description,
    cover_picture_url: coverPictureUrl,
    mode,
  } = program;
  const { open, close, isOpen } = useToggle();
  const breakpoint = useBreakpoint();
  const theme = useTheme();
  const { ref: descriptionRef, isTruncated: hasLongDescription } =
    useTextTruncation(description);

  const displayStatusButton =
    mode !== 'admin_match' &&
    status !== undefined &&
    currentUser?.can_be_a_mentor;

  // Split description into lines and render with proper line breaks
  const descriptionLines = description?.split('\n') || [];

  return (
    <>
      <MainWrapper data-testid="mentoring-page-header">
        {breakpoint === 's' ? (
          <Box
            justifyContent={coverPictureUrl ? 'space-between' : 'flex-end'}
            width="100%"
            alignItems="start"
            mb={5}
          >
            <ImageStyled width="220px" src={coverPictureUrl} alt="" />
          </Box>
        ) : (
          <ImageStyled
            width={{ m: '227px', l: '222px', xl: '209px' }}
            src={coverPictureUrl}
            alt=""
          />
        )}
        <Box
          ml={{ _: 0, m: 5, l: 6 }}
          flexDirection="column"
          justifyContent="space-between"
          width="100%"
        >
          <ThemeProvider
            theme={theme.themeName}
            fontBrightness={theme.themeVariables.appHeaderFontBrightness}
          >
            <WrapperTitleAndDescription>
              <Title>{name}</Title>
              {descriptionLines.length > 0 && (
                <Description ref={descriptionRef}>
                  {descriptionLines.map((line, index) => (
                    <Fragment key={index}>
                      {line}
                      {index < descriptionLines.length - 1 && <br />}
                    </Fragment>
                  ))}
                </Description>
              )}
              {hasLongDescription && (
                <FakeLink onClick={open}>{__('See more')}</FakeLink>
              )}
            </WrapperTitleAndDescription>
          </ThemeProvider>
          {displayStatusButton && (
            <StatusButton
              status={status}
              disabledAccess={disabledAccess}
              {...program}
            />
          )}
        </Box>
      </MainWrapper>
      {isOpen && (
        <FolderDetailsModal
          name={name}
          description={description}
          coverPictureFileUrl={coverPictureUrl}
          onCloseModal={close}
          title={name}
        />
      )}
    </>
  );
};

export default PageHeader;

const MainWrapper = styled(Box).attrs({
  display: 'flex',
  flexDirection: { _: 'column', m: 'row' },
  alignItems: 'start',
  mb: { _: 6, l: 8 },
  mt: 6,
})``;

const ImageStyled = styled(Image)`
  aspect-ratio: 650 / 364;
  object-fit: cover;
  border-radius: 10px;
`;

const WrapperTitleAndDescription = styled(Box).attrs({
  width: { _: '100%', m: '75%' },
  display: 'block',
  mb: { _: 3, m: 4 },
})``;

const Title = styled(Box).attrs({
  display: 'block',
  font: 'h2-semibold',
  mb: 2,
})`
  overflow-wrap: anywhere;
`;

const Description = styled(Box)`
  font: 'body-regular-asphalt';
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: 2;
  text-overflow: ellipsis;
  -webkit-box-orient: vertical;
`;
